import React, { useState, useEffect, useCallback } from "react";
import styles from "./style.module.scss";
import { Avatar, Text, Button, Spinner, Icons } from "@evvve/ui-kit";
import eventGroupChatIcon from "src/assets/icons/event_group_chat.svg";
import { Dialog, DialogType, MessageType, SystemMessageType } from "../../../types";
import ContextMenu, { ContextMenuTypes } from "src/shared-features/ContextMenu";
import QuitChatModal from "../../../features/QuitChatModal";
import { useModal } from "src/hooks";
import { getTimeIn12hFormat } from "src/helpers/date";
import { applyLongPress } from "../../../helpers";

const contextMenuQuitChatTypes = {
  [DialogType.Personal]: ContextMenuTypes.DeleteDialog,
  [DialogType.Group]: ContextMenuTypes.LeaveGroup,
};

const deleteDialogButtonStatics = {
  [DialogType.Personal]: "Delete",
  [DialogType.Group]: "Leave",
};

const systemMessageStatics = {
  [SystemMessageType.IAmSpeakerAtTheEvent]: "I'm a speaker at the event",
  [SystemMessageType.InvitationToGoLive]: "Invitation to go live",
  [SystemMessageType.WelcomeToEvvve]: "Welcome to Evvve!",
  [SystemMessageType.TheEventStartsIn3Hours]: "The event starts in 3 hours",
  [SystemMessageType.TheEventStartsIn15Min]: "The event starts in 15 min",
  [SystemMessageType.TheEventIsLive]: "The event is live!",
  [SystemMessageType.IAmOrganizingTheEvent]: "I am organizing the event",
  [SystemMessageType.IWillNotBeASpeakerAtTheEvent]: "I won’t be a speaker at the event",
  [SystemMessageType.IAcceptedToBeASpeakerAtTheEvent]: "I accepted to be a speaker at the event",
  [SystemMessageType.WelcomeToTheEvent]: "Welcome to the event",
  [SystemMessageType.TheEventHasBeenCancelled]: "The event has been cancelled",
  [SystemMessageType.NewSpeakerAtTheEvent]: "New speaker at the event",
  [SystemMessageType.EventDetailsHaveChanged]: "Event details have changed",
  [SystemMessageType.YouHaveBeenInvitedToBeASpeaker]: "You've been invited to be a speaker",
  [SystemMessageType.EventDurationExceeded]: "Event duration exceeded",
};

const {
  AlertCircle
} = Icons;

const {
  wrap,
  inner,
  info,
  // time_style,
  unread,
  name_style,
  message_style,
  // check,
  warning,
  row,
  row_left_side,
  row_right_side,
  avatar,
} = styles;

interface Props {
  dialog: Dialog;
  chooseChatHandler?: () => void;
  quitChatHandler?: () => void;
  isQuitChatLoading?: boolean;
}

const ChatItem = ({ dialog, chooseChatHandler, quitChatHandler, isQuitChatLoading }: Props) => {
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [
    contextMenuPosition,
    setContextMenuPosition] = useState<{ left: number; top: number; } | null>(null);

  const dlm = dialog.lastMessage;
  let dialogLastMessageText = null;
  if (dlm)
    if (dlm.type === MessageType.User)
      dialogLastMessageText = dlm.text;
    else if (dlm.type === MessageType.System)
      dialogLastMessageText = systemMessageStatics[dlm.systemMessageType];

  useEffect(() => {
    if(isContextMenuOpen) {

      const closeContextMenu = () => setIsContextMenuOpen(false);

      document.addEventListener("scroll", closeContextMenu);
      window.addEventListener("resize", closeContextMenu);
      return () => {
        document.removeEventListener("scroll", closeContextMenu);
        window.removeEventListener("resize", closeContextMenu);
      };
    }

  }, [isContextMenuOpen]);

  const {
    isOpenModal: isQuitChatModalOpen,
    openModal: openQuitChatModal,
    closeModal: closeQuitChatModal,
  } = useModal();

  const longPressHandler = useCallback((e: TouchEvent) => {
    setContextMenuPosition({
      left: e.touches[0].clientX,
      top: e.touches[0].clientY,
    });
    setIsContextMenuOpen(true);
  }, []);

  return (
    <>
      <div className={wrap}>
        <div
          className={inner}
          onClick={() => chooseChatHandler?.()}
          onContextMenu={(e) => {
            e.preventDefault();
            if (!dialog.system) {
              setContextMenuPosition({
                left: e.clientX,
                top: e.clientY,
              });
              setIsContextMenuOpen(true);
            }
          }}
          ref={(node) => {
            //long press context menu
            node && applyLongPress(node, longPressHandler);
          }}
        >
          <Avatar
            src={dialog.avatarUrl || eventGroupChatIcon}
            size="m"
            status={dialog.isOnline ? "online" : "offline"}
            alt={`Icon: ${dialog.title}`}
            className={avatar}
          />
          <div className={info}>
            <header className={row}>
              <div className={row_left_side}>
                {dialog.title &&
                <div className={name_style}>
                  <Text size="m" color="grayscale1000">
                    {dialog.title}
                  </Text>
                </div>
                }
                {/* {(chat.isVerified && chat.type === DialogType.Personal) && (
                  <UserCheck size="s" color="grayscale600" />
                )} */}
              </div>
              <div className={row_right_side}>
                {dialog.type === DialogType.Group && (
                  <Text size="xs" color="grayscale600">
                  Event&nbsp;group
                  </Text>
                )}
                {/* <div className={check}>
                  <Check color={isRead ? "violet500" : "grayscale400"} />
                </div> */}
                {dialog.lastMessage?.datetime && <div className={styles.time_style}>
                  <Text color="grayscale400" size="xs">
                    {getTimeIn12hFormat(dialog.lastMessage.datetime)}
                  </Text>
                </div>}
              </div>
            </header>
            <footer className={row}>
              <div className={row_left_side}>
                {dialog.isWarning ? dialogLastMessageText && (
                  <div className={warning}>
                    <AlertCircle size="s" color="tangerine1000" />
                    <Text size="xs" color="tangerine1000">
                      {dialogLastMessageText}
                    </Text>
                  </div>
                ) : dialogLastMessageText && (
                  <div className={message_style}>
                    <Text size="xs" color="grayscale600">
                      {dialogLastMessageText}
                    </Text>
                  </div>
                )}
              </div>
              {!!dialog.unreadMessagesCount &&
                <div className={unread}>
                  <Text color="white" size="xs">
                    {dialog.unreadMessagesCount}
                  </Text>
                </div>
              }
            </footer>
          </div>
        </div>
      </div>
      {isContextMenuOpen && <ContextMenu
        onClickBlockingArea={() => setIsContextMenuOpen(false)}
        position={{
          left: contextMenuPosition?.left,
          top: contextMenuPosition?.top,
        }}
        actions={[
          {
            type: contextMenuQuitChatTypes[dialog.type],
            handler: () => {
              setIsContextMenuOpen(false);
              openQuitChatModal();
            }
          }
        ]}
      />}
      {isQuitChatModalOpen &&
      <QuitChatModal
        onClose={closeQuitChatModal}
        dialogType={dialog.type}
        dialogAvatarUrl={dialog.avatarUrl}
        dialogCaption={dialog.title}
        buttons={
          <div className={styles.leaveGroupModalButtons}>
            <Button
              type="outline"
              theme="violet"
              onClick={closeQuitChatModal}
            >
              Cancel
            </Button>
            <Button
              type="solid"
              theme="violet"
              onClick={quitChatHandler}
            >
              {isQuitChatLoading ? <Spinner size="m" /> : deleteDialogButtonStatics[dialog.type]}
            </Button>
          </div>
        }
      />}
    </>
  );
};

export default ChatItem;